// FriendFeed.js — Friend activity feed for NeevStar
import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../supabase';

const ACTIVITY_ICONS = {
  chest: '📦', certificate: '🎓', streak: '🔥',
  levelup: '⬆️', world: '🌍', quiz: '🏆', default: '⭐',
};

function timeAgo(ts) {
  const diff = Math.floor((Date.now() - new Date(ts)) / 1000);
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export function postActivity({ userId, childName, neevbotLevel, activityType, activityDetail, coinsEarned = 0 }) {
  if (!userId) return;
  supabase.from('neevstar_activity_feed').insert({
    user_id: userId,
    child_name: childName,
    neevbot_level: neevbotLevel || 1,
    activity_type: activityType,
    activity_detail: activityDetail,
    coins_earned: coinsEarned,
  }).then(() => {}).catch(() => {});
}

export default function FriendFeed({ user, profile, onAddFriend }) {
  const [tab, setTab] = useState('feed'); // 'feed' | 'friends' | 'add'
  const [feed, setFeed] = useState([]);
  const [friends, setFriends] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchUsername, setSearchUsername] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const [searchMsg, setSearchMsg] = useState('');
  const [searching, setSearching] = useState(false);
  const [sending, setSending] = useState(false);

  const loadFriends = useCallback(async () => {
    if (!user?.id) return;
    const { data } = await supabase
      .from('neevstar_friends')
      .select('*')
      .or(`user_id.eq.${user.id},friend_id.eq.${user.id}`)
      .eq('status', 'accepted');
    setFriends(data || []);
    return data || [];
  }, [user?.id]);

  const loadFeed = useCallback(async () => {
    if (!user?.id) { setLoading(false); return; }
    setLoading(true);
    try {
      const friendRows = await loadFriends();
      const friendIds = friendRows.map(r => r.user_id === user.id ? r.friend_id : r.user_id);
      if (friendIds.length === 0) { setFeed([]); setLoading(false); return; }
      const since = new Date(Date.now() - 48 * 3600 * 1000).toISOString();
      const { data } = await supabase
        .from('neevstar_activity_feed')
        .select('*')
        .in('user_id', friendIds)
        .gte('created_at', since)
        .order('created_at', { ascending: false })
        .limit(30);
      setFeed(data || []);
    } catch (_) {}
    setLoading(false);
  }, [user?.id, loadFriends]);

  useEffect(() => { loadFeed(); }, [loadFeed]);

  const handleSearch = async () => {
    if (!searchUsername.trim()) return;
    setSearching(true); setSearchResult(null); setSearchMsg('');
    const { data } = await supabase
      .from('bm_child_profiles')
      .select('child_name,neev_username,neevbot_level,streak_count,is_founding_explorer,founding_explorer_number,xp_points')
      .eq('neev_username', searchUsername.trim())
      .single();
    setSearching(false);
    if (data) setSearchResult(data);
    else setSearchMsg('No user found with that username.');
  };

  const sendRequest = async () => {
    if (!searchResult || !user?.id) return;
    setSending(true);
    // Look up friend's auth_user_id stored in bm_child_profiles
    const { data: fp } = await supabase
      .from('bm_child_profiles')
      .select('auth_user_id, child_name')
      .eq('neev_username', searchResult.neev_username)
      .single();
    if (!fp?.auth_user_id) {
      setSending(false);
      setSearchMsg('Could not find that user. Ask them to log in once to set up their profile.');
      return;
    }
    const { error } = await supabase.from('neevstar_friends').insert({
      user_id: user.id,
      friend_id: fp.auth_user_id,
      status: 'pending',
    });
    setSending(false);
    if (!error) setSearchMsg(`Friend request sent to ${searchResult.child_name}! ✅`);
    else setSearchMsg('Already sent or an error occurred.');
  };

  const acceptRequest = async (row) => {
    await supabase.from('neevstar_friends').update({ status: 'accepted' }).eq('id', row.id);
    loadFeed();
  };

  const levelNames = ['','Curious Cub','Smart Starter','Market Explorer','Deal Maker','Brand Builder','Team Leader','Digital Pioneer','Impact Maker','Scale Champion','Young CEO'];

  return (
    <div style={{ fontFamily: "'Nunito', sans-serif" }}>
      {/* Tabs */}
      <div style={{ display: 'flex', gap: 6, padding: '12px 16px 8px', borderBottom: '1px solid #F0F0F0' }}>
        {[['feed','📡 Feed'],['friends','👥 Friends'],['add','➕ Add']].map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)} style={{
            flex: 1, padding: '8px 4px', borderRadius: 12, border: 'none', cursor: 'pointer',
            background: tab === id ? 'linear-gradient(135deg,#0066FF,#8B2BE2)' : '#F0F0F0',
            color: tab === id ? 'white' : '#666', fontWeight: 800, fontSize: 12,
            fontFamily: "'Nunito', sans-serif",
          }}>{label}</button>
        ))}
      </div>

      {/* Feed tab */}
      {tab === 'feed' && (
        <div style={{ padding: '12px 16px' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 32, color: '#AAA', fontWeight: 700 }}>Loading feed...</div>
          ) : feed.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 16px' }}>
              <p style={{ fontSize: 40, margin: '0 0 12px' }}>🌟</p>
              <p style={{ fontSize: 15, fontWeight: 800, color: '#888', margin: '0 0 6px' }}>No friend activity yet</p>
              <p style={{ fontSize: 13, color: '#AAA', fontWeight: 700, margin: '0 0 16px' }}>
                Invite friends to see their adventures here!
              </p>
              <button onClick={() => setTab('add')} style={{
                padding: '10px 20px', borderRadius: 20, border: 'none', cursor: 'pointer',
                background: 'linear-gradient(135deg,#0066FF,#8B2BE2)', color: 'white',
                fontWeight: 800, fontSize: 13, fontFamily: "'Nunito', sans-serif",
              }}>➕ Add Friends</button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {feed.map(item => (
                <div key={item.id} style={{
                  background: 'white', borderRadius: 14, padding: '12px 14px',
                  display: 'flex', alignItems: 'center', gap: 10,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.06)', border: '1px solid #F0F0F0',
                }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                    background: 'linear-gradient(135deg,#0066FF,#8B2BE2)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 18,
                  }}>
                    {ACTIVITY_ICONS[item.activity_type] || ACTIVITY_ICONS.default}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <p style={{ margin: 0, fontSize: 13, fontWeight: 800, color: '#1A1A2E' }}>
                      {item.child_name}
                      <span style={{ fontSize: 11, color: '#888', fontWeight: 700 }}>
                        {' '}· Lv{item.neevbot_level || 1}
                      </span>
                    </p>
                    <p style={{ margin: 0, fontSize: 12, color: '#555', fontWeight: 700 }}>
                      {item.activity_detail}
                    </p>
                    {item.coins_earned > 0 && (
                      <p style={{ margin: 0, fontSize: 11, color: '#FF9500', fontWeight: 800 }}>
                        +{item.coins_earned} 🪙
                      </p>
                    )}
                  </div>
                  <span style={{ fontSize: 11, color: '#AAA', fontWeight: 700, flexShrink: 0 }}>
                    {timeAgo(item.created_at)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Friends tab */}
      {tab === 'friends' && (
        <div style={{ padding: '12px 16px' }}>
          {friends.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '40px 16px' }}>
              <p style={{ fontSize: 40, margin: '0 0 12px' }}>👥</p>
              <p style={{ fontSize: 15, fontWeight: 800, color: '#888' }}>No friends yet</p>
              <p style={{ fontSize: 13, color: '#AAA', fontWeight: 700 }}>Add friends using their NeevStar username</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {friends.map(f => (
                <div key={f.id} style={{
                  background: 'white', borderRadius: 14, padding: '12px 14px',
                  display: 'flex', alignItems: 'center', gap: 10,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
                }}>
                  <div style={{
                    width: 40, height: 40, borderRadius: '50%',
                    background: 'linear-gradient(135deg,#8B2BE2,#0066FF)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 20, flexShrink: 0,
                  }}>🤖</div>
                  <div style={{ flex: 1 }}>
                    <p style={{ margin: 0, fontSize: 14, fontWeight: 800, color: '#1A1A2E' }}>Friend</p>
                    <p style={{ margin: 0, fontSize: 12, color: '#888', fontWeight: 700 }}>{f.status}</p>
                  </div>
                  {f.status === 'pending' && f.friend_id === user?.id && (
                    <button onClick={() => acceptRequest(f)} style={{
                      padding: '6px 14px', borderRadius: 10, border: 'none', cursor: 'pointer',
                      background: '#00C851', color: 'white', fontWeight: 800, fontSize: 12,
                      fontFamily: "'Nunito', sans-serif",
                    }}>Accept</button>
                  )}
                </div>
              ))}
            </div>
          )}
          <div style={{ marginTop: 12, padding: '10px 14px', background: '#F8F9FF', borderRadius: 14, border: '1px solid #E8E8FF' }}>
            <p style={{ margin: 0, fontSize: 12, fontWeight: 700, color: '#888' }}>Your username:</p>
            <p style={{ margin: 0, fontSize: 15, fontWeight: 900, color: '#0066FF' }}>
              {profile?.neev_username || 'Not set yet'}
            </p>
          </div>
        </div>
      )}

      {/* Add friends tab */}
      {tab === 'add' && (
        <div style={{ padding: '16px' }}>
          <p style={{ fontSize: 13, fontWeight: 700, color: '#888', margin: '0 0 12px' }}>
            Search by your friend's NeevStar username
          </p>
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            <input
              value={searchUsername}
              onChange={e => setSearchUsername(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleSearch()}
              placeholder="e.g. NeevExplorer_1234"
              style={{
                flex: 1, padding: '10px 14px', borderRadius: 12,
                border: '2px solid #E0E0E0', fontSize: 14, fontFamily: "'Nunito', sans-serif",
                fontWeight: 700, outline: 'none',
              }}
            />
            <button onClick={handleSearch} disabled={searching} style={{
              padding: '10px 16px', borderRadius: 12, border: 'none', cursor: 'pointer',
              background: 'linear-gradient(135deg,#0066FF,#8B2BE2)', color: 'white',
              fontWeight: 800, fontSize: 13, fontFamily: "'Nunito', sans-serif",
            }}>
              {searching ? '...' : '🔍'}
            </button>
          </div>
          {searchMsg && (
            <p style={{ fontSize: 13, fontWeight: 700, color: searchMsg.includes('✅') ? '#00C851' : '#FF2D55', margin: '0 0 12px' }}>
              {searchMsg}
            </p>
          )}
          {searchResult && (
            <div style={{
              background: 'white', borderRadius: 16, padding: '16px',
              boxShadow: '0 4px 16px rgba(0,0,0,0.08)', border: '2px solid #E8E8FF',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
                <div style={{
                  width: 48, height: 48, borderRadius: '50%',
                  background: 'linear-gradient(135deg,#8B2BE2,#0066FF)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24,
                }}>🤖</div>
                <div>
                  <p style={{ margin: 0, fontSize: 16, fontWeight: 900, color: '#1A1A2E' }}>
                    {searchResult.child_name}
                    {searchResult.is_founding_explorer && <span style={{ color: '#FFD600', marginLeft: 6 }}>⚜️</span>}
                  </p>
                  <p style={{ margin: 0, fontSize: 12, color: '#888', fontWeight: 700 }}>
                    {levelNames[Math.min(searchResult.neevbot_level || 1, 10)]} · 🔥 {searchResult.streak_count || 0} days
                  </p>
                </div>
              </div>
              <button onClick={sendRequest} disabled={sending} style={{
                width: '100%', padding: '12px', borderRadius: 12, border: 'none', cursor: 'pointer',
                background: 'linear-gradient(135deg,#0066FF,#8B2BE2)', color: 'white',
                fontWeight: 800, fontSize: 14, fontFamily: "'Nunito', sans-serif",
              }}>
                {sending ? 'Sending...' : '👋 Send Friend Request'}
              </button>
            </div>
          )}
          <div style={{ marginTop: 20, padding: '14px', background: '#FFF8E1', borderRadius: 14, border: '1px solid #FFE082' }}>
            <p style={{ margin: 0, fontSize: 12, fontWeight: 700, color: '#E65100' }}>Share your username:</p>
            <p style={{ margin: 0, fontSize: 15, fontWeight: 900, color: '#FF6B00' }}>
              {profile?.neev_username || 'Loading...'}
            </p>
            {profile?.neev_username && (
              <button onClick={() => {
                const text = `Add me on NeevStar! My username is ${profile.neev_username} 🌟 neevstar.com`;
                if (navigator.share) navigator.share({ text });
                else if (navigator.clipboard) { navigator.clipboard.writeText(text); }
              }} style={{
                marginTop: 8, padding: '6px 14px', borderRadius: 10, border: 'none', cursor: 'pointer',
                background: '#FF6B00', color: 'white', fontWeight: 800, fontSize: 12,
                fontFamily: "'Nunito', sans-serif",
              }}>📤 Share Username</button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
